// lib/list/factoryFolderMentions.ts
import type { KernelHandle } from '#types/types';
import { normalizeFactoryPersonaHandle } from './factoryPersonas';

// Skip code, links and URLs just as output mentions do. Labels are presentation, never authority.
const token = /(?<tildes>~{3,})[\s\S]*?(?:\k<tildes>|$)|(?<ticks>`+)[\s\S]*?(?:\k<ticks>|$)|!?\[[^\]\n]*\]\([^)\n]*\)|https?:\/\/[^\s]+|(?<prefix>^|[\s(*_])(?<mention>@folder:(?<id>[a-z0-9%_~-]+)(?:\[(?<label>[^\]\s]*)\])?)(?=$|[\s),.;:!?*_])/gi;

export function factoryFolderMentions(text: string) {
  return [...text.matchAll(token)].flatMap(match => {
    if (!match.groups?.mention) return [];
    try {
      const id = decodeURIComponent(match.groups.id!);
      const name = match.groups.label === undefined ? undefined : decodeURIComponent(match.groups.label);
      if (!id || id.length > 120 || (name !== undefined && (!name || name.length > 180))) return [];
      return [{ id, name, token: match.groups.mention, start: match.index + (match.groups.prefix?.length ?? 0) }];
    } catch { return []; }
  });
}

export function captureFactoryFolderMention(kernel: KernelHandle, projectId: string, folder: { id: string; name: string; displayName?: string }, draft: string) {
  const state = kernel.getState();
  const userId = state.userContext?.dbUserId;
  const current = state.chatFactoryFolderMentions;
  const sameScope = current?.userId === userId && current?.projectId === projectId;
  if (!userId || (!sameScope && factoryFolderMentions(draft).some(item => item.name === undefined))) {
    throw new Error('Select the folder again for this account and List project.');
  }
  const folders = sameScope ? [...current.folders] : [];
  let bound = folders.find(item => item.id === folder.id);
  if (!bound) {
    const slug = normalizeFactoryPersonaHandle(folder.name) || 'folder';
    let alias = slug;
    let suffix = 2;
    while (folders.some(item => item.alias === alias)) alias = `${slug}-${suffix++}`;
    bound = { ...folder, alias };
    folders.push(bound);
  } else {
    bound = { ...bound, ...folder, displayName: folder.displayName };
    folders[folders.findIndex(item => item.id === folder.id)] = bound;
  }
  kernel.setState({ chatFactoryFolderMentions: { projectId, userId, folders } });
  return `folder:${bound.alias}`;
}

export function serializeFactoryFolderMentions(kernel: KernelHandle, projectId: string, text: string) {
  const state = kernel.getState();
  const scope = state.chatFactoryFolderMentions;
  let result = text;
  for (const item of factoryFolderMentions(text).reverse()) {
    if (item.name !== undefined) continue;
    const folder = scope?.folders.find(folder => folder.alias === item.id);
    if (!folder || scope?.projectId !== projectId || scope.userId !== state.userContext?.dbUserId) {
      throw new Error('A folder mention is unavailable in this draft. Select it from @ again.');
    }
    const wire = `@folder:${encodeURIComponent(folder.id).replace(/\./g, '%2E')}[${encodeURIComponent(folder.displayName ?? folder.name)}]`;
    result = result.slice(0, item.start) + wire + result.slice(item.start + item.token.length);
  }
  return result;
}

export function removeFactoryFolderMention(text: string, id: string) {
  let result = text;
  for (const item of factoryFolderMentions(text).reverse()) {
    if (item.id === id) result = result.slice(0, item.start) + result.slice(item.start + item.token.length).replace(/^ /, '');
  }
  return result;
}
